// Session-scoped persistence for the fulfillment bar. The selection's
// carriers between pages are the URL query and location.state, and both are
// lost on bare navigations (header links, the restaurant page's back button,
// reloads) — so every remount used to reset the bar to today/ASAP. This keeps
// the last selection for the tab's lifetime as a fallback of last resort:
// URL params and navigation state always take priority over it.
import { getAppNow } from './demoTime';
import { coordsToTimeZone, wallClockToIso } from './timezoneUtils';
import { fdebug } from './fulfillmentDebug';

const KEY = 'mealops:fulfillment';
// Schema version, stored with the payload: bump when a saved field's meaning
// changes and old entries must not be trusted. Loads ignore any entry whose
// version doesn't match instead of half-reading a stale shape.
const SCHEMA_VERSION = 1;

// `timeZone`: the zone the slot's date/time are wall clock in, when the page
// knows it (the menu page: the restaurant's). See loadSessionFulfillment.
export const saveSessionFulfillment = (fulfillment, { timeZone = null } = {}) => {
  if (!fulfillment) return;
  try {
    fdebug('session.save', 'persisting', fulfillment);
    sessionStorage.setItem(
      KEY,
      JSON.stringify({
        v: SCHEMA_VERSION,
        service: fulfillment.service ?? null,
        address: fulfillment.address ?? '',
        coords: fulfillment.coords ?? null,
        date: fulfillment.date ?? null,
        time: fulfillment.time ?? null,
        asap: fulfillment.asap === true,
        mealType: fulfillment.mealType ?? null,
        timeZone: timeZone || null,
      })
    );
  } catch {}
};

// The team whose addresses the discovery bar last preselected against. The
// header's team switcher is on every page, so a switch made elsewhere followed
// by a bare navigation back to discovery restores the previous team's address
// from the session, and that has to read as a switch too. Kept in
// sessionStorage next to that restored address (module memory alone was lost
// on a full page load, e.g. right after Team Setup made a new team active, so
// the new team kept the old team's hotel); module memory is the fallback when
// storage is blocked.
const LAST_PRESELECT_TEAM_KEY = 'mealops:discovery:lastPreselectTeam';
let lastPreselectTeamIdMemory = null;
export const readLastPreselectTeamId = () => {
  try {
    const stored = window.sessionStorage.getItem(LAST_PRESELECT_TEAM_KEY);
    if (stored) return stored;
  } catch {
    // storage blocked: fall through to memory
  }
  return lastPreselectTeamIdMemory;
};
export const writeLastPreselectTeamId = (teamId) => {
  lastPreselectTeamIdMemory = teamId;
  try {
    window.sessionStorage.setItem(LAST_PRESELECT_TEAM_KEY, teamId);
  } catch {
    // storage blocked: memory still covers in-app navigation
  }
};

// A restored address that discovery set aside on mount because it was
// checked for another team (see useDiscoveryFulfillment). The bar takes it
// back once the new team's addresses are in, if it is one of them.
let addressHeldForTeamCheck = null;
export const holdAddressForTeamCheck = (held) => {
  addressHeldForTeamCheck = held?.address ? held : null;
};
export const takeAddressHeldForTeamCheck = () => {
  const held = addressHeldForTeamCheck;
  addressHeldForTeamCheck = null;
  return held;
};

// Sign-out drops the selection: the address and coords are the coach's, not
// the tab's (see signOutStorage.js).
export const clearSessionFulfillment = () => {
  addressHeldForTeamCheck = null;
  try {
    sessionStorage.removeItem(KEY);
  } catch {}
};

// Restores the tab's last fulfillment. Two independent parts:
//
// - Identity (service, address, coords, mealType) ALWAYS restores — losing
//   the delivery address on a bare navigation just because the session was
//   ASAP was the original sin here.
// - The time slot restores only when it is a scheduled, still-upcoming one.
//   An ASAP selection reads as "no slot" (a fresh mount's default is already
//   ASAP, re-resolved to the current soonest), and a stale (past) slot would
//   just trigger the bar's "we moved your time" snap — both come back as
//   `date: null, time: null, asap: true`. The slot is a wall clock at the
//   fulfillment, not on the device, so the past-check reads it in the zone
//   it was saved with (the restaurant's, from a menu page), or else the
//   stored address's (discovery reads it the same way). Device-local only
//   with neither. A pickup slot has no coords, and without the saved zone a
//   9:15 AM picked at a Pacific store was dropped on the next bare visit from
//   an Eastern laptop, where 9:15 had gone by (2026-10-13). It is still a
//   heuristic: the bar re-validates against the restaurant's zone.
export const loadSessionFulfillment = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) {
      fdebug('session.load', 'nothing stored');
      return null;
    }
    const stored = JSON.parse(raw);
    if (!stored || typeof stored !== 'object') return null;
    // Version gate: entries written before versioning (no `v`) still load —
    // v1 kept their shape — but a future-versioned entry is ignored whole.
    if (stored.v != null && stored.v !== SCHEMA_VERSION) {
      fdebug('session.load', 'schema version mismatch — ignoring', { stored: stored.v, expected: SCHEMA_VERSION });
      return null;
    }

    let slotValid = stored.asap !== true && Boolean(stored.date && stored.time);
    if (slotValid) {
      const slotIso = wallClockToIso(
        stored.date,
        String(stored.time).slice(0, 5),
        stored.timeZone || coordsToTimeZone(stored.coords),
      );
      const slotMs = slotIso ? Date.parse(slotIso) : NaN;
      slotValid = Number.isFinite(slotMs) && slotMs > getAppNow().getTime();
    }

    const restored = {
      // Only a service the pages know. A tab that saved a bad one from a
      // hand-edited URL heals on its next mount instead of keeping it.
      service: stored.service === 'delivery' || stored.service === 'pickup' ? stored.service : null,
      address: stored.address ?? '',
      coords: stored.coords ?? null,
      mealType: stored.mealType ?? null,
      date: slotValid ? stored.date : null,
      time: slotValid ? stored.time : null,
      asap: !slotValid,
    };
    fdebug(
      'session.load',
      slotValid ? 'restoring identity + scheduled slot' : 'restoring identity only (slot ASAP/past)',
      restored
    );
    return restored;
  } catch {
    return null;
  }
};
